import React, { useMemo } from 'react';
import { Page, Text, View, Document, StyleSheet, Font } from '@react-pdf/renderer';

// --- Helper Functions (Reused) ---
const formatNumber = (val) => Number(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const isNumericHeader = (header) => {
    if (header.toUpperCase().includes('SALES_PERSON') || header.toUpperCase().includes('SALESPERSON') || header.toUpperCase().includes('CUSTOMER')) {
        return false;
    }
    return ['QTY', 'AMOUNT', 'U.PRICE','COMM_DISC','VAT%','AMT', 'COMMISSION', 'SALES', 'RETURN', 'VAT', 'TOTAL', '%'].some(s => header.toUpperCase().includes(s));
};

const FOOTER_TOTAL_KEYS = ['QTY', 'AMOUNT', 'VATAMT'];

// --- NEW Helper Function for Fixed Widths ---
const getColumnWidths = (totalColumns) => {
    // 1. Define relative weights (e.g., Descriptive column is 2 units, Data columns are 1 unit)
    const descriptiveWeight = 2;
    const standardWeight = 1;
    
    // totalColumns includes the descriptive column
    const dataColumnsCount = totalColumns - 1; 

    // 2. Calculate the total weight
    const totalWeight = descriptiveWeight + (dataColumnsCount * standardWeight);
    
    // 3. Calculate percentage widths
    const descriptiveWidth = (descriptiveWeight / totalWeight) * 100;
    const standardWidth = (standardWeight / totalWeight) * 100;

    return {
        descriptiveWidth: `${descriptiveWidth}%`,
        standardWidth: `${standardWidth}%`,
        totalWeight,
    };
};

// --- Styling Definitions ---
const BORDER_COLOR = '#999'; 
const BORDER_WIDTH = 1;
const FOOTER_BACKGROUND = '#b3b3b3';

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#fff',
    padding: 20,
  },
  title: {
    fontSize: 16,
    marginBottom: 10,
    textAlign: 'center',
    fontWeight: 'bold',
  },
  table: { 
    display: 'table', 
    width: '100%', // Ensure table uses full page width
  },
  tableRow: { 
    flexDirection: 'row', 
    width: '100%', // Ensure row uses full table width
    alignItems: 'center',
  },
  tableHeader: { 
    backgroundColor: '#f0f4c3',
    fontWeight: 'bold',
  },
  // Base Cell Styles
  tableCell: { 
    borderColor: BORDER_COLOR,
    borderStyle: 'solid', 
    borderBottomWidth: BORDER_WIDTH,
    borderRightWidth: BORDER_WIDTH,
    borderLeftWidth: 0, 
    borderTopWidth: 0,
    
    padding: 5, 
    fontSize: 8, 
    // Removed flexGrow/minWidth here
  },
  // Specific Cell Overrides
  descriptiveCell: {
    textAlign: 'left',
    borderLeftWidth: BORDER_WIDTH,
  },
  numericCell: {
    textAlign: 'right',
  },
  // Footer Styles
  tableFooterRow: { 
    flexDirection: 'row', 
    width: '100%',
    backgroundColor: FOOTER_BACKGROUND,
    borderTopWidth: 3, 
    borderTopColor: '#000', 
  },
  footerCell: {
    fontSize: 10,
    fontWeight: 'bold',
    textAlign: 'right',
    padding: 8,
    // Removed border properties here
  }
});

// --- PDF Component (Modified) ---
const SalesReportContent = ({ rows, descriptiveColumnKey, reportTitle = "Aggregated Sales Report" }) => { 

    // 1. Dynamically determine all display columns
    const allKeys = rows.length > 0 ? Object.keys(rows[0]) : [];
    const displayKeys = allKeys.filter(key =>
        !['isGroupHeader', 'isSubHeader', 'isSubSubHeader', 'totalAmount', 'totalAmountExclVAT', descriptiveColumnKey].includes(key)
    );
    
    // 2. Calculate FIXED WIDTHS based on column count
    const totalColumns = 1 + displayKeys.length;
    const { descriptiveWidth: descriptiveColWidth, standardWidth: standardColWidth } = getColumnWidths(totalColumns);

    // 3. Calculate the Grand Totals
    const internalGrandTotals = useMemo(() => {
        if (rows.length === 0) return {};
        const keysToTotal = displayKeys.filter(key => FOOTER_TOTAL_KEYS.includes(key));
        const totals = {};
        for (const key of keysToTotal) {
            totals[key] = rows.reduce((sum, row) => sum + (Number(row[key]) || 0), 0);
        }
        return totals;
    }, [rows, displayKeys]);
    
    const totalValueKeys = displayKeys.filter(key => FOOTER_TOTAL_KEYS.includes(key));
    const descriptiveHeader = descriptiveColumnKey.replace(/([A-Z])/g, ' $1').trim().toUpperCase();

    // 4. Calculate Footer Span Width
    let descriptiveSpanIndex = 0; // Index of the first column *after* the descriptive column
    if (totalValueKeys.length > 0) {
        const firstTotalKey = totalValueKeys[0];
        descriptiveSpanIndex = displayKeys.indexOf(firstTotalKey);
        if (descriptiveSpanIndex === -1) descriptiveSpanIndex = displayKeys.length;
    }
    
    // Descriptive span width includes the descriptive column plus all non-total columns before the first total column
    const totalDescriptiveWidth = parseFloat(descriptiveColWidth) + (descriptiveSpanIndex * parseFloat(standardColWidth));
    
    if (rows.length === 0) return <Text style={{ textAlign: 'center' }}>No sales data found for the selected filters.</Text>;

    return (
        <View style={styles.table}>
            {/* Header Row */}
            <View style={[styles.tableRow, styles.tableHeader]}>
                {/* 1. Descriptive Header (Fixed Width) */}
                <Text style={[styles.tableCell, styles.descriptiveCell, { width: descriptiveColWidth }]}>
                    {descriptiveHeader}
                </Text>
                {/* 2. Dynamic Headers (Fixed Width) */}
                {displayKeys.map(key => (
                    <Text
                        key={key}
                        style={[
                            styles.tableCell, 
                            isNumericHeader(key) ? styles.numericCell : styles.descriptiveCell,
                            { width: standardColWidth } // 👈 FIX: Apply fixed width here
                        ]}
                    >
                        {key.replace(/([A-Z])/g, ' $1').trim().toUpperCase()}
                    </Text>
                ))}
            </View>

            {/* Body Rows */}
            {rows.map((row, index) => (
                <View key={`agg-row-${index}`} style={styles.tableRow}>
                    {/* 1. Descriptive Cell (Fixed Width) */}
                    <Text style={[styles.tableCell, styles.descriptiveCell, { backgroundColor: '#ffffff', width: descriptiveColWidth }]}>
                        {row[descriptiveColumnKey]}
                    </Text>
                    {/* 2. Data Cells (Fixed Width) */}
                    {displayKeys.map(key => (
                        <Text 
                            key={key} 
                            style={[
                                styles.tableCell, 
                                isNumericHeader(key) ? styles.numericCell : styles.descriptiveCell,
                                { width: standardColWidth } // 👈 FIX: Apply fixed width here
                            ]}
                        >
                            {isNumericHeader(key) ? formatNumber(row[key]) : row[key]}
                        </Text>
                    ))}
                </View>
            ))}

            {/* Footer - Grand Totals */}
            {totalValueKeys.length > 0 && (
                <View style={styles.tableFooterRow}>
                    {/* 1. GRAND TOTAL label cell (Width based on calculated span) */}
                    <Text style={[
                        styles.footerCell, 
                        { 
                            width: `${totalDescriptiveWidth}%`, // 👈 FIX: Apply calculated width
                            textAlign: 'right', 
                            paddingRight: 10,
                            // BORDER FIX: Manually define borders for merging
                            borderLeftWidth: BORDER_WIDTH,
                            borderRightWidth: BORDER_WIDTH, 
                            borderColor: BORDER_COLOR,
                            borderStyle: 'solid',
                        }
                    ]}>
                        GRAND TOTAL:
                    </Text>
                    
                    {/* 2. Total cells (Width is standardColWidth) */}
                    {displayKeys.slice(descriptiveSpanIndex).map((key) => {
                        const isTotalKey = FOOTER_TOTAL_KEYS.includes(key);
                        
                        return (
                            <Text 
                                key={key} 
                                style={[
                                    styles.footerCell, 
                                    styles.numericCell, 
                                    { 
                                        width: standardColWidth, // 👈 FIX: Apply standard width
                                        // BORDER FIX: Define right border
                                        borderRightWidth: BORDER_WIDTH,
                                        borderColor: BORDER_COLOR,
                                        borderStyle: 'solid',
                                    } 
                                ]}
                            >
                                {isTotalKey ? formatNumber(internalGrandTotals[key]) : ''}
                            </Text>
                        );
                    })}
                </View>
            )}
        </View>
    );
};

// Wrapper Document Component
const SalesTableAggregatedPDF = ({ rows, descriptiveColumnKey, reportTitle }) => (
  <Document>
    <Page size="A4" style={styles.page} orientation="landscape">
        <Text style={styles.title}>{reportTitle}</Text>
        <SalesReportContent rows={rows} descriptiveColumnKey={descriptiveColumnKey} reportTitle={reportTitle} />
    </Page>
  </Document>
);

export default SalesTableAggregatedPDF;